// Module: session
// Contract: ./contract.md   Spec: docs/vault-spec-v2.md §5.2, §7.1–7.4, §7.7
//
// The sequencing layer. Runs the ceremony once and fans its two outputs out; holds the unlocked
// keys; gives them up on lock. No DOM.

import type { WebAuthn, Assertion } from "../webauthn_2026-08-27/webauthn.js"
import type { RemoteStorage, Session as AuthSession } from "../remote-storage_2026-08-27/remote-storage.js"
import * as record from "../bootstrap-record_2026-08-27/bootstrap-record.js"
import * as keys from "../key-hierarchy_2026-08-27/key-hierarchy.js"
import { createEmpty, fromBytes, type VaultStore } from "../vault-store_2026-08-27/vault-store.js"
import {
  generate as generatePhrase,
  normalize as normalizeSecret,
  validate as validatePhrase,
} from "../passphrase_2026-08-27/passphrase.js"

export type SessionState = "locked" | "busy" | "unlocked"
export type LockReason = "explicit" | "idle" | "error"

/** `sealed` and `slotId` are what make this machine able to open the vault without the key. */
export type Adoption = { vaultId: string; slotId: string; sealed: Uint8Array }
/**
 * `credential` is returned so the caller can create a *second* named vault on the same key. An
 * assertion never yields a public key — only `createCredential` does — so a machine that has only
 * ever adopted cannot supply one, and would spend a fresh credential slot instead (spec §8.2).
 */
export type EnrolResult = Adoption & {
  recoveryPhrase: string
  credential: { credentialId: Uint8Array; publicKey: Uint8Array }
}

/**
 * Words, not a base32 code. It lives on paper in a safe place and gets transcribed by hand under
 * stress, which is exactly the condition a 54-symbol string is worst at. Eight words is ~103 bits
 * — far beyond reach even though the wrapped DEK it protects is publicly readable — and it reuses
 * the normalisation that already makes a phrase survive being retyped on a different keyboard.
 */
export const RECOVERY_WORDS = 8

/** The recovery phrase's bytes, fed to HKDF exactly as the code's decoded bytes used to be. */
const recoveryBytes = (phrase: string): Uint8Array => enc.encode(normalizeSecret(phrase))

export type SessionErrorCode =
  | "WhenLocked"
  | "VaultExists"
  | "VaultNotFound"
  | "NoMatchingSlot"
  | "RecordTampered"
  | "ConflictDetected"

export class SessionError extends Error {
  readonly code: SessionErrorCode
  constructor(code: SessionErrorCode, message?: string) {
    super(message ?? code)
    this.name = "SessionError"
    this.code = code
  }
}

export const DEFAULT_IDLE_TIMEOUT_MS = 10 * 60 * 1000

export type SessionDeps = {
  webauthn: WebAuthn
  remote: RemoteStorage
  clock?: () => number
  idleTimeoutMs?: number
  onLock?: (reason: LockReason) => void
  now?: () => string
  uuid?: () => string
}

export interface VaultSession {
  readonly state: SessionState
  readonly vaultId: string | null
  /** The credential that opened this session, so the caller can avoid the chooser next time. */
  readonly credentialId: Uint8Array | null
  /** `existingCredential` reuses a key's credential for a second named vault (spec §8.2). */
  enrol(opts: {
    name: string
    userName: string
    password: string
    existingCredential?: { credentialId: Uint8Array; publicKey: Uint8Array }
  }): Promise<EnrolResult>
  /** Zero ceremonies: everything needed is the password and this machine's sealed secret. */
  unlock(opts: { vaultId: string; slotId: string; password: string; sealed: Uint8Array }): Promise<void>
  /** One ceremony. Teaches a new machine to open the vault, then behaves like unlock. */
  adopt(opts: { name: string; password: string }): Promise<Adoption>
  unlockWithRecoveryPhrase(opts: {
    vaultId: string
    phrase: string
    password: string
  }): Promise<Adoption>
  /**
   * Enrols an additional security key against the same vault. Two ceremonies, done once.
   *
   * Takes the vault's `name` rather than remembering it: a session opened by password alone
   * (§7.3) never learned one, and persisting it would put the name in the clear on the machine.
   */
  addKey(opts: { name: string; password: string; label: string }): Promise<void>
  store(): VaultStore
  save(): Promise<void>
  touch(): void
  lock(reason?: LockReason): void
}

type Unlocked = {
  vaultId: string
  auth: AuthSession
  bootstrap: record.BootstrapRecord
  dek: CryptoKey
  contentKey: CryptoKey
  macKey: CryptoKey
  /** The KEK and wrapped DEK of the slot this session opened with. `rewrapDek` needs both to
   *  enrol another slot, because the unwrapped DEK is non-extractable by design. */
  slotKek: CryptoKey
  slotWrappedDek: Uint8Array
  credentialId: Uint8Array | null
  store: VaultStore
  vaultEtag: string | null
  recordEtags: Map<string, string>
  revision: bigint
  lastTouched: number
}

const enc = new TextEncoder()

/** Vault objects are immutable, so every save needs a fresh address (spec §6.4). */
function randomObjectId(): string {
  return Array.from(crypto.getRandomValues(new Uint8Array(32)), (b) =>
    b.toString(16).padStart(2, "0"),
  ).join("")
}

const b64u = (bytes: Uint8Array): string => {
  let binary = ""
  for (const b of bytes) binary += String.fromCharCode(b)
  return btoa(binary).replace(/\+/gu, "-").replace(/\//gu, "_").replace(/=+$/u, "")
}

/** Ed25519 seeds are wrapped in a minimal PKCS#8 envelope so WebCrypto will import them. */
function pkcs8FromEd25519Seed(seed: Uint8Array): Uint8Array {
  const prefix = Uint8Array.from([
    0x30, 0x2e, 0x02, 0x01, 0x00, 0x30, 0x05, 0x06, 0x03, 0x2b, 0x65, 0x70, 0x04, 0x22, 0x04, 0x20,
  ])
  const out = new Uint8Array(prefix.length + seed.length)
  out.set(prefix, 0)
  out.set(seed, prefix.length)
  return out
}

export function createSession(deps: SessionDeps): VaultSession {
  const clock = deps.clock ?? (() => Date.now())
  const nowIso = deps.now ?? (() => new Date().toISOString())
  const uuid = deps.uuid ?? (() => crypto.randomUUID())
  const idleTimeoutMs = deps.idleTimeoutMs ?? DEFAULT_IDLE_TIMEOUT_MS

  let open: Unlocked | null = null
  let busy = false

  const requireOpen = (): Unlocked => {
    if (!open) throw new SessionError("WhenLocked", "the vault is locked")
    if (clock() - open.lastTouched > idleTimeoutMs) {
      lock("idle")
      throw new SessionError("WhenLocked", "the vault locked on idle")
    }
    return open
  }

  function lock(reason: LockReason = "explicit"): void {
    if (!open) return
    // Best-effort only: JavaScript cannot guarantee erasure (spec §1.2). Dropping the CryptoKey
    // references is what actually matters — they are non-extractable, so once unreachable the key
    // material is gone with them.
    open = null
    deps.onLock?.(reason)
  }

  /**
   * One `credentials.get()`, evaluating a single PRF salt derived from the vault's name, whose
   * output is split into the locator and the hardware secret (spec §2.2, §3.3).
   *
   * The name is what lets one credential address many vaults, which matters because a discoverable
   * credential costs a slot the user may not be able to prune (§8.2). Only `eval.first` is
   * requested: a platform that forwards one PRF salt but not two still works here, and the
   * previous two-salt design would have failed outright on one.
   */
  async function secretsFor(opts: {
    name: string
    allowCredentialIds?: Uint8Array[]
  }): Promise<{ assertion: Assertion; locator: Uint8Array; hard: Uint8Array }> {
    const prfInput = await record.prfInputFor(opts.name)
    const challenge = await deps.remote.getChallenge()
    const result = await deps.webauthn.assert({
      challenge,
      ...(opts.allowCredentialIds ? { allowCredentialIds: opts.allowCredentialIds } : {}),
      prfInputs: { first: prfInput },
    })
    if (!result.prf) {
      throw new Error("assert() returned no PRF output despite being asked for one")
    }
    const { locator, hard } = await keys.splitPrfSeed(result.prf.first)
    return { assertion: result.assertion, locator, hard }
  }

  async function deviceAuthKeys(opts: {
    password: string
    kdf: keys.KdfParams
    hard: Uint8Array
  }): Promise<{ signingKey: CryptoKey; publicKey: Uint8Array }> {
    const seed = await keys.deviceAuthSeed({
      passphrase: opts.password,
      kdf: opts.kdf,
      hardSecret: opts.hard,
    })
    const signingKey = await crypto.subtle.importKey(
      "pkcs8",
      pkcs8FromEd25519Seed(seed) as unknown as BufferSource,
      { name: "Ed25519" },
      false,
      ["sign"],
    )
    return { signingKey, publicKey: await derivePublicFromSeed(seed) }
  }

  async function registerDeviceAuth(opts: {
    vaultId: string
    password: string
    kdf: keys.KdfParams
    hard: Uint8Array
    auth: AuthSession
  }): Promise<void> {
    const { publicKey } = await deviceAuthKeys(opts)
    await deps.remote.registerCredential({
      vaultId: opts.vaultId,
      credentialId: publicKey,
      publicKey,
      session: opts.auth,
      kind: "ed25519",
    })
  }

  /** Authenticates with no ceremony at all: both factors are already in hand. */
  async function authenticateWithDeviceKey(opts: {
    vaultId: string
    password: string
    kdf: keys.KdfParams
    hard: Uint8Array
  }): Promise<AuthSession> {
    const { signingKey, publicKey } = await deviceAuthKeys(opts)
    const challenge = b64u(await deps.remote.getChallenge())
    const signature = new Uint8Array(
      await crypto.subtle.sign(
        { name: "Ed25519" },
        signingKey,
        enc.encode(challenge) as unknown as BufferSource,
      ),
    )
    return deps.remote.authenticate({
      kind: "ed25519",
      vaultId: opts.vaultId,
      challenge,
      signature,
      publicKey,
    })
  }

  async function loadRecord(
    vaultId: string,
  ): Promise<{ record: record.BootstrapRecord; mac: Uint8Array; etag: string }> {
    let fetched: { bytes: Uint8Array; etag: string }
    try {
      fetched = await deps.remote.getRecord(vaultId)
    } catch (err) {
      if ((err as { code?: string }).code === "NotFound") {
        throw new SessionError("VaultNotFound", `no vault at ${vaultId}`)
      }
      throw err
    }
    const parsed = record.parse(fetched.bytes)
    return { record: parsed.record, mac: parsed.mac, etag: fetched.etag }
  }

  /**
   * Writes a record to every locator address (spec §2.4) and returns the new etags.
   *
   * It does not touch session state: `save` must be able to fail here without having already
   * advanced its own idea of the current revision or object.
   */
  async function writeRecordEverywhere(
    state: Unlocked,
    next: record.BootstrapRecord,
  ): Promise<Map<string, string>> {
    const mac = await record.computeMac(next, state.macKey)
    const bytes = record.serialize(next, mac)
    const etags = new Map(state.recordEtags)
    for (const address of record.locatorAddresses(next)) {
      const result = await deps.remote.putRecord({
        vaultId: address,
        bytes,
        session: state.auth,
        ifMatch: etags.get(address) ?? null,
      })
      etags.set(address, result.etag)
    }
    return etags
  }

  async function openWith(opts: {
    vaultId: string
    auth: AuthSession
    bootstrap: record.BootstrapRecord
    mac: Uint8Array
    recordEtag: string
    dek: CryptoKey
    slotKek: CryptoKey
    slotWrappedDek: Uint8Array
    credentialId: Uint8Array | null
  }): Promise<void> {
    const macKey = await keys.deriveSubkey(opts.dek, "record-mac")

    // After the unwrap, never before: the MAC key comes from the DEK, so an earlier check would be
    // circular. Tampering with objectId or revision is a live rollback attempt (spec §2.5).
    if (!(await record.verifyMac(opts.bootstrap, opts.mac, macKey))) {
      throw new SessionError("RecordTampered", "the bootstrap record failed its integrity check")
    }

    const contentKey = await keys.deriveSubkey(opts.dek, "content")
    const vault = await deps.remote.getVault({
      vaultId: opts.vaultId,
      objectId: opts.bootstrap.objectId,
      session: opts.auth,
      key: contentKey,
    })

    open = {
      vaultId: opts.vaultId,
      auth: opts.auth,
      bootstrap: opts.bootstrap,
      dek: opts.dek,
      contentKey,
      macKey,
      slotKek: opts.slotKek,
      slotWrappedDek: opts.slotWrappedDek,
      credentialId: opts.credentialId,
      store: fromBytes(vault.plaintext),
      vaultEtag: vault.etag,
      recordEtags: new Map([[opts.vaultId, opts.recordEtag]]),
      revision: vault.revision,
      lastTouched: clock(),
    }
  }

  /** Any failure locks first and throws second: a half-open session must not exist. */
  async function guarded<T>(fn: () => Promise<T>): Promise<T> {
    busy = true
    try {
      return await fn()
    } catch (err) {
      lock("error")
      throw err
    } finally {
      busy = false
    }
  }

  return {
    get state(): SessionState {
      if (busy) return "busy"
      return open ? "unlocked" : "locked"
    },

    get vaultId(): string | null {
      return open?.vaultId ?? null
    },

    get credentialId(): Uint8Array | null {
      return open?.credentialId ?? null
    },

    async enrol({ name, userName, password, existingCredential }): Promise<EnrolResult> {
      return guarded(async () => {
        const recoveryPhrase = generatePhrase({ words: RECOVERY_WORDS })

        // Reusing an existing credential is the point of named vaults. A discoverable credential
        // costs a slot that a Titan cannot prune without factory-resetting the whole key (§8.2),
        // so a second vault must not create a second credential. The caller supplies the public
        // key because an assertion never returns one — only createCredential does.
        const credential =
          existingCredential ??
          (await deps.webauthn.createCredential({
            kind: "roaming",
            userId: crypto.getRandomValues(new Uint8Array(16)),
            userName,
            userDisplayName: userName,
          }))

        // ONE ceremony, ONE PRF salt derived from the name, split into both secrets (§2.2, §3.3).
        const evaluated = await secretsFor({ name, allowCredentialIds: [credential.credentialId] })
        const vaultId = await record.deriveVaultId(evaluated.locator)

        // A 200 here means this credential already has a vault. Stop rather than overwrite it.
        try {
          await deps.remote.getRecord(vaultId)
          throw new SessionError("VaultExists", `a vault already exists at ${vaultId}`)
        } catch (err) {
          if (err instanceof SessionError) throw err
          if ((err as { code?: string }).code !== "NotFound") throw err
        }

        const dekBytes = keys.generateDek()
        const kdf = keys.generateKdfParams()
        const objectId = randomObjectId()

        // The Worker is given a key derived from this vault's PRF seed and the password — never
        // the WebAuthn credential. That key is different for every vault, so nothing the server
        // holds links a user's vaults to each other; registering the credential's public key
        // would have put identical bytes in every one of them (spec §1.2, §3.7).
        const device = await deviceAuthKeys({ password, kdf, hard: evaluated.hard })
        await deps.remote.registerCredential({
          vaultId,
          credentialId: device.publicKey,
          publicKey: device.publicKey,
          session: null, // trust on first use (spec §1.5)
          kind: "ed25519",
        })
        const auth = await authenticateWithDeviceKey({
          vaultId,
          password,
          kdf,
          hard: evaluated.hard,
        })

        // Authenticator slot — no second ceremony; the hard secret came from the call above.
        const slotSalt = keys.generateHkdfSalt()
        const slotId = uuid()
        const slotKek = await keys.deriveKek({
          passphrase: password,
          secondFactor: evaluated.hard,
          hkdfSalt: slotSalt,
          slotId,
          kdf,
        })

        // Recovery slot: the same shape, with the code standing in for the authenticator.
        const recoverySalt = keys.generateHkdfSalt()
        const recoverySlotId = uuid()
        const recoverySecond = await keys.recoverySecret(recoveryBytes(recoveryPhrase), recoverySalt)
        const recoveryKek = await keys.deriveKek({
          passphrase: password,
          secondFactor: recoverySecond,
          hkdfSalt: recoverySalt,
          slotId: recoverySlotId,
          kdf,
        })
        const recoverySeed = await keys.recoverySigningSeed(recoveryBytes(recoveryPhrase), recoverySalt)
        const recoveryPublic = await derivePublicFromSeed(recoverySeed)
        await deps.remote.registerCredential({
          vaultId,
          credentialId: recoveryPublic,
          publicKey: recoveryPublic,
          session: auth,
          kind: "ed25519",
        })

        const created = record.create({
          vaultId,
          objectId,
          kdf,
          slots: [
            {
              id: slotId,
              type: "roaming",
              label: "Primary authenticator",
              credentialId: credential.credentialId,
              locatorVaultId: vaultId,
              hkdfSalt: slotSalt,
              wrappedDek: await keys.wrapDek({ kek: slotKek, dek: dekBytes }),
              createdAt: nowIso(),
            },
            {
              id: recoverySlotId,
              type: "recovery",
              label: "Recovery code",
              hkdfSalt: recoverySalt,
              wrappedDek: await keys.wrapDek({ kek: recoveryKek, dek: dekBytes }),
              createdAt: nowIso(),
            },
          ],
        })

        const dek = await keys.unwrapDek({
          kek: slotKek,
          wrappedDek: created.slots[0]!.wrappedDek,
        })
        const contentKey = await keys.deriveSubkey(dek, "content")
        const macKey = await keys.deriveSubkey(dek, "record-mac")
        const emptyStore = createEmpty()

        // Object first, then the record that references it (spec §5.5).
        const written = await deps.remote.putVault({
          vaultId,
          objectId,
          session: auth,
          key: contentKey,
          plaintext: emptyStore.toBytes(),
          revision: 1n,
        })

        open = {
          vaultId,
          auth,
          bootstrap: record.withRevision(created, 1n),
          dek,
          contentKey,
          macKey,
          slotKek,
          slotWrappedDek: created.slots[0]!.wrappedDek,
          credentialId: credential.credentialId,
          store: emptyStore,
          vaultEtag: written.etag,
          recordEtags: new Map(),
          revision: 1n,
          lastTouched: clock(),
        }
        open.recordEtags = await writeRecordEverywhere(open, open.bootstrap)

        const sealed = await keys.sealHardSecret({ passphrase: password, kdf, hardSecret: evaluated.hard })
        return {
          vaultId,
          slotId,
          sealed,
          recoveryPhrase,
          credential: {
            credentialId: credential.credentialId,
            publicKey: credential.publicKey,
          },
        }
      })
    },

    /**
     * The everyday path, and the point of the whole redesign: no WebAuthn ceremony, no touch, no
     * PIN. The hardware secret was sealed under the password when this machine was adopted, so the
     * password alone reconstitutes it.
     *
     * Two factors remain — the password, and this machine holding the sealed blob. The blob is
     * never uploaded, so the stored ciphertext is not attackable without one of them.
     */
    async unlock({ vaultId, slotId, password, sealed }): Promise<void> {
      return guarded(async () => {
        const loaded = await loadRecord(vaultId)

        const slot = record.findSlot(loaded.record, { slotId })
        if (!slot) throw new SessionError("NoMatchingSlot", "this machine's slot is gone")

        // A wrong password fails here, before anything touches the network.
        const hard = await keys.openHardSecret({
          passphrase: password,
          kdf: loaded.record.kdf,
          sealed,
        })

        const kek = await keys.deriveKek({
          passphrase: password,
          secondFactor: hard,
          hkdfSalt: slot.hkdfSalt,
          slotId: slot.id,
          kdf: loaded.record.kdf,
        })
        const dek = await keys.unwrapDek({ kek, wrappedDek: slot.wrappedDek })

        const auth = await authenticateWithDeviceKey({
          vaultId,
          password,
          kdf: loaded.record.kdf,
          hard,
        })

        await openWith({
          vaultId,
          auth,
          bootstrap: loaded.record,
          mac: loaded.mac,
          recordEtag: loaded.etag,
          dek,
          slotKek: kek,
          slotWrappedDek: slot.wrappedDek,
          credentialId: slot.credentialId ?? null,
        })
      })
    },

    /** One ceremony. Teaches this machine the hardware secret, then opens as usual. */
    async adopt({ name, password }): Promise<Adoption> {
      return guarded(async () => {
        const evaluated = await secretsFor({ name })
        const vaultId = await record.deriveVaultId(evaluated.locator)
        const loaded = await loadRecord(vaultId)

        const slot = record.findSlot(loaded.record, {
          credentialId: evaluated.assertion.credentialId,
        })
        if (!slot) throw new SessionError("NoMatchingSlot", "this key is not enrolled for that vault")

        const kek = await keys.deriveKek({
          passphrase: password,
          secondFactor: evaluated.hard,
          hkdfSalt: slot.hkdfSalt,
          slotId: slot.id,
          kdf: loaded.record.kdf,
        })
        const dek = await keys.unwrapDek({ kek, wrappedDek: slot.wrappedDek })
        // Not the assertion: the Worker never sees WebAuthn material (§1.2). The key that signs
        // here is derived from the same PRF seed the ceremony just produced, so possession is
        // proved transitively — the seed is obtainable only through a verified ceremony at the
        // pinned origin (§8.1).
        const auth = await authenticateWithDeviceKey({
          vaultId,
          password,
          kdf: loaded.record.kdf,
          hard: evaluated.hard,
        })

        await openWith({
          vaultId,
          auth,
          bootstrap: loaded.record,
          mac: loaded.mac,
          recordEtag: loaded.etag,
          dek,
          slotKek: kek,
          slotWrappedDek: slot.wrappedDek,
          credentialId: evaluated.assertion.credentialId,
        })

        const sealed = await keys.sealHardSecret({
          passphrase: password,
          kdf: loaded.record.kdf,
          hardSecret: evaluated.hard,
        })
        return { vaultId, slotId: slot.id, sealed }
      })
    },

    async unlockWithRecoveryPhrase({ vaultId, phrase, password }): Promise<Adoption> {
      return guarded(async () => {
        // A phrase has no checksum, so this is what replaces one: every word must be in the
        // wordlist, which catches a typo before any network call and names the offending word.
        // Arguably better than a checksum, which can only say "something is wrong".
        const verdict = validatePhrase(phrase)
        if (!verdict.valid) {
          throw new SessionError(
            "NoMatchingSlot",
            verdict.reason === "unknown-word"
              ? `"${verdict.badWord}" is not a word from the recovery phrase`
              : `that is not a complete recovery phrase (${verdict.reason})`,
          )
        }
        const payload = recoveryBytes(phrase)
        const loaded = await loadRecord(vaultId)

        const slot = record.findSlot(loaded.record, { type: "recovery" })
        if (!slot) throw new SessionError("NoMatchingSlot", "this vault has no recovery slot")

        const seed = await keys.recoverySigningSeed(payload, slot.hkdfSalt)
        const publicKey = await derivePublicFromSeed(seed)
        const signingKey = await crypto.subtle.importKey(
          "pkcs8",
          pkcs8FromEd25519Seed(seed) as unknown as BufferSource,
          { name: "Ed25519" },
          false,
          ["sign"],
        )

        const challenge = await deps.remote.getChallenge()
        const challengeText = b64u(challenge)
        const signature = new Uint8Array(
          await crypto.subtle.sign(
            { name: "Ed25519" },
            signingKey,
            enc.encode(challengeText) as unknown as BufferSource,
          ),
        )
        const auth = await deps.remote.authenticate({
          kind: "ed25519",
          vaultId,
          challenge: challengeText,
          signature,
          publicKey,
        })

        const secondFactor = await keys.recoverySecret(payload, slot.hkdfSalt)
        const kek = await keys.deriveKek({
          passphrase: password,
          secondFactor,
          hkdfSalt: slot.hkdfSalt,
          slotId: slot.id,
          kdf: loaded.record.kdf,
        })
        const dek = await keys.unwrapDek({ kek, wrappedDek: slot.wrappedDek })

        await openWith({
          vaultId,
          auth,
          bootstrap: loaded.record,
          mac: loaded.mac,
          recordEtag: loaded.etag,
          dek,
          slotKek: kek,
          slotWrappedDek: slot.wrappedDek,
          credentialId: null, // recovery uses no authenticator
        })

        // Adopt this machine against the recovery slot, so the next open needs neither the code
        // nor the hardware key — just the password.
        const sealed = await keys.sealHardSecret({
          passphrase: password,
          kdf: loaded.record.kdf,
          hardSecret: secondFactor,
        })
        await registerDeviceAuth({
          vaultId,
          password,
          kdf: loaded.record.kdf,
          hard: secondFactor,
          auth,
        })
        return { vaultId, slotId: slot.id, sealed }
      })
    },

    store(): VaultStore {
      return requireOpen().store
    },

    /**
     * Vault objects are immutable, so a save is: write a NEW object, then repoint the record at it
     * (spec §6.4). Two consequences worth stating.
     *
     * A corrupt or interrupted write lands at an address nothing references, so the revision the
     * record still points at survives untouched — which is what replaces the object versioning R2
     * does not have.
     *
     * Concurrency serialises on one thing, the record's `If-Match`, rather than two conditional
     * writes that could disagree. Two devices both mint objects; one wins the record write and the
     * loser's object is simply never referenced.
     */
    async save(): Promise<void> {
      const state = requireOpen()
      const nextRevision = state.revision + 1n
      const nextObjectId = randomObjectId()

      const written = await deps.remote.putVault({
        vaultId: state.vaultId,
        objectId: nextObjectId,
        session: state.auth,
        key: state.contentKey,
        plaintext: state.store.toBytes(),
        revision: nextRevision,
      })

      const nextRecord = record.withObjectId(
        record.withRevision(state.bootstrap, nextRevision),
        nextObjectId,
      )

      let etags: Map<string, string>
      try {
        etags = await writeRecordEverywhere(state, nextRecord)
      } catch (err) {
        if ((err as { code?: string }).code === "PreconditionFailed") {
          // Never retried, never merged: the caller reloads and presents the conflict (§6.2).
          // The object just written is orphaned and pruned by lifecycle; nothing local advanced.
          throw new SessionError("ConflictDetected", "the vault changed elsewhere; reload first")
        }
        throw err
      }

      // Commit only once every copy of the record is written. The checkpoint moves here, with
      // the record, because the record is what makes a revision current.
      await deps.remote.commitRevision(state.vaultId, nextRevision)
      state.revision = nextRevision
      state.vaultEtag = written.etag
      state.bootstrap = nextRecord
      state.recordEtags = etags
      state.store.markClean()
      state.lastTouched = clock()
    },

    /**
     * Enrols a second security key.
     *
     * Nothing about the vault key changes: the DEK is random and generated once, and every slot
     * wraps that same DEK under its own KEK. A new key simply gets a new slot. `rewrapDek` moves
     * the DEK across inside `key-hierarchy`, so the unlocked DEK — non-extractable by design —
     * never has to be readable here.
     *
     * Two ceremonies, because `create()` cannot return PRF results and a `get()` is required.
     * Done once per key.
     */
    async addKey({ name, password, label }): Promise<void> {
      const state = requireOpen()

      const existing = state.bootstrap.slots
        .map((slot) => slot.credentialId)
        .filter((id): id is Uint8Array => id !== undefined)

      const credential = await deps.webauthn.createCredential({
        kind: "roaming",
        userId: crypto.getRandomValues(new Uint8Array(16)),
        userName: label,
        userDisplayName: label,
        // Refuses to enrol a key that already has a slot, which would otherwise silently create a
        // duplicate and burn a discoverable-credential slot on a key that cannot prune them.
        excludeCredentialIds: existing,
      })

      const evaluated = await secretsFor({ name, allowCredentialIds: [credential.credentialId] })
      const locatorVaultId = await record.deriveVaultId(evaluated.locator)

      // A second key yields a different PRF seed, hence a different `KeyHard` and a different
      // derived key. Each slot registers its own, and none of them is credential-shaped.
      const device = await deviceAuthKeys({
        password,
        kdf: state.bootstrap.kdf,
        hard: evaluated.hard,
      })
      await deps.remote.registerCredential({
        vaultId: state.vaultId,
        credentialId: device.publicKey,
        publicKey: device.publicKey,
        session: state.auth,
        kind: "ed25519",
      })

      const hkdfSalt = keys.generateHkdfSalt()
      const slotId = uuid()
      const kek = await keys.deriveKek({
        passphrase: password,
        secondFactor: evaluated.hard,
        hkdfSalt,
        slotId,
        kdf: state.bootstrap.kdf,
      })
      const wrappedDek = await keys.rewrapDek({
        fromKek: state.slotKek,
        toKek: kek,
        wrappedDek: state.slotWrappedDek,
      })

      const withKey = record.addSlot(state.bootstrap, {
        id: slotId,
        type: "roaming",
        label,
        credentialId: credential.credentialId,
        locatorVaultId,
        hkdfSalt,
        wrappedDek,
        createdAt: nowIso(),
      })
      state.recordEtags = await writeRecordEverywhere(state, withKey)
      state.bootstrap = withKey
      state.lastTouched = clock()
    },

    touch(): void {
      if (open) open.lastTouched = clock()
    },

    lock(reason: LockReason = "explicit"): void {
      lock(reason)
    },
  }
}

/** Ed25519 public key (SPKI) for a seed, via a throwaway import. */
async function derivePublicFromSeed(seed: Uint8Array): Promise<Uint8Array> {
  const key = await crypto.subtle.importKey(
    "pkcs8",
    pkcs8FromEd25519Seed(seed) as unknown as BufferSource,
    { name: "Ed25519" },
    true,
    ["sign"],
  )
  const jwk = (await crypto.subtle.exportKey("jwk", key)) as { x?: string }
  if (!jwk.x) throw new Error("could not derive the recovery public key")
  const publicKey = await crypto.subtle.importKey(
    "jwk",
    { kty: "OKP", crv: "Ed25519", x: jwk.x },
    { name: "Ed25519" },
    true,
    ["verify"],
  )
  return new Uint8Array(await crypto.subtle.exportKey("spki", publicKey))
}
